import styles from './FeaturesGroups.module.css';

export default function PrivateSharingGroups() {
  return (
    <div className={styles.container}>
      <div className={styles.overlay} />
      <div className={styles.content}>
        <h1 className={styles.title}>Private Sharing for Groups</h1>

        <div className={styles.introSection}>
          <p className={styles.subtitle}>
            Share contact information and updates with specific groups of peoplewithout handing your
            data to a third-party platform.
          </p>
        </div>

        <div className={styles.featuresContainer}>
          <div className={styles.feature}>
            <h2>Invite-Only Group Sharing</h2>
            <p>
              Create private groups where only invited members can see shared information. You
              decide exactly who is in the group and what they can see.
            </p>
          </div>

          <div className={styles.feature}>
            <h2>Control What You Share</h2>
            <p>
              Choose which fields to share with a groupfor example, share your email and phone with
              close collaborators, but only your public profile with a wider circle.
            </p>
          </div>

          <div className={styles.feature}>
            <h2>Group-Specific Views</h2>
            <p>
              Each group gets its own view of your information, so you can keep personal, family,
              professional, and community roles clearly separated.
            </p>
          </div>

          <div className={styles.feature}>
            <h2>No Centralized Social Graph</h2>
            <p>
              Your relationships stay in your database. There is no advertising network or hidden
              profile building happening behind the scenes.
            </p>
          </div>
        </div>

        <div className={styles.useCasesSection}>
          <h2>How It Works</h2>
          <div className={styles.feature}>
            <p>
              Group sharing is designed so you can send a curated set of contacts to specific people
              without exposing your entire database or handing your relationship graph to a third
              party. The server is used for delivery, but the shared contact data is encrypted before
              it leaves your device.
            </p>
          </div>

          <div className={styles.feature}>
            <img
              src="/secret-exchange.svg"
              alt="Diagram showing encrypted group sharing between two users via a secure server"
              style={{ width: '100%', height: 'auto', borderRadius: 12 }}
            />
          </div>

          <div className={styles.useCasesGrid}>
            <div className={styles.useCase}>
              <h3>1) Set your Public ID</h3>
              <ul>
                <li>Mark a contact as "Me" in the app.</li>
                <li>Publish a Public ID so others can find you.</li>
              </ul>
            </div>
            <div className={styles.useCase}>
              <h3>2) Share a group</h3>
              <ul>
                <li>Select a group and choose what to include.</li>
                <li>The app encrypts the shared data before sending.</li>
              </ul>
            </div>
            <div className={styles.useCase}>
              <h3>3) Recipient imports securely</h3>
              <ul>
                <li>The recipient retrieves the message using their Public ID.</li>
                <li>They decrypt and import the shared contacts into their database.</li>
              </ul>
            </div>
          </div>

          <div className={styles.feature}>
            <h3>What the server can (and cannot) see</h3>
            <p>
              The server stores and delivers an encrypted payload. It can see delivery metadata (for
              example, who the message is addressed to and when it was created), but it does not need
              to see your plaintext contact data to make group sharing work.
            </p>
          </div>
        </div>

        <div className={styles.useCasesSection}>
          <h2>Keeping a Person Updated from a Profile ID</h2>
          <div className={styles.feature}>
            <p>
              If a person record includes a stable <strong>Profile ID</strong> (the other person's
              public identifier), you can support a "Refresh from Profile" workflow:
            </p>
            <ul>
              <li>
                Store the profile identifier on the person record (for example, on a field like
                <code>profileId</code> or <code>externalPublicId</code>).
              </li>
              <li>
                When the user clicks refresh (or on a schedule), fetch the latest profile data for
                that ID from your profile service.
              </li>
              <li>
                Merge updates into the person record with clear rules (only update fields the user has
                opted into, and avoid overwriting user-entered local notes).
              </li>
            </ul>
          </div>

          <div className={styles.feature}>
            <h3>Recommended merge rule</h3>
            <p>
              Track per-field provenance (local vs profile-sourced) and only auto-update fields that
              are marked as profile-sourced. If a user edits a field locally, flip that field to
              local ownership so a future refresh does not overwrite it.
            </p>
          </div>

          <div className={styles.feature}>
            <h3>Privacy / consent model</h3>
            <p>
              This works best when the profile owner explicitly controls what fields are published and
              which audiences can see them. The Profile ID becomes a pointer to "check for updates",
              not a backdoor to pull everything.
            </p>
          </div>
        </div>

        <div className={styles.useCasesSection}>
          <h2>How Private Group Sharing Helps</h2>
          <div className={styles.useCasesGrid}>
            <div className={styles.useCase}>
              <h3>Families & Friends</h3>
              <ul>
                <li>Share updated addresses and phone numbers only within your family group.</li>
                <li>Keep event-specific contact lists for reunions, weddings, and trips.</li>
              </ul>
            </div>

            <div className={styles.useCase}>
              <h3>Project Teams</h3>
              <ul>
                <li>Maintain a private roster of teammates, roles, and key details.</li>
                <li>Share only what the team needs, not your entire personal network.</li>
              </ul>
            </div>

            <div className={styles.useCase}>
              <h3>Community & Volunteer Groups</h3>
              <ul>
                <li>
                  Give organizers access to the right contact details without exposing everything.
                </li>
                <li>Manage changing roles and responsibilities while keeping history.</li>
              </ul>
            </div>

            <div className={styles.useCase}>
              <h3>Professional Circles</h3>
              <ul>
                <li>Share professional contact information with curated groups of peers.</li>
                <li>Keep your personal and professional identities cleanly separated.</li>
              </ul>
            </div>
          </div>
        </div>

        <div className={styles.benefitsSection}>
          <h2>Why This Matters</h2>
          <div className={styles.benefitsGrid}>
            <div className={styles.benefit}>
              <h3>
                <span role="img" aria-label="lock"></span> Real Privacy
              </h3>
              <p>
                Sharing happens on your terms. There is no opaque, centralized service watching who
                you know and how you interact.
              </p>
            </div>

            <div className={styles.benefit}>
              <h3>
                <span role="img" aria-label="shield"></span> Safer Coordination
              </h3>
              <p>
                Coordinate events, projects, and responsibilities without pushing everyone into yet
                another social platform.
              </p>
            </div>

            <div className={styles.benefit}>
              <h3>
                <span role="img" aria-label="people"></span> Respect for Relationships
              </h3>
              <p>
                Treat the people you know as real relationshipsnot as ad targets. Groups are a way
                to collaborate, not to monetize.
              </p>
            </div>

            <div className={styles.benefit}>
              <h3>
                <span role="img" aria-label="gear"></span> Fits Into Your Workflow
              </h3>
              <p>
                Private sharing builds on the groups and profiles you already keep in
                MyPeopleDatabase, instead of forcing a new system.
              </p>
            </div>
          </div>
        </div>

        <div className={styles.ctaSection}>
          <h2>Start Using Private Sharing for Groups</h2>
          <p>
            Begin by organizing your contacts into meaningful groups in MyPeopleDatabase. From
            there, you can selectively share the right information with the right people.
          </p>
          <div className={styles.ctaButtons}>
            <a href="/features-groups" className={styles.secondaryButton}>
              Learn more about Groups
            </a>
            <a href="/buy" className={styles.primaryButton}>
              Get MyPeopleDatabase
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
